import type { Anniversary, AnniversaryIcon, AnniversaryKind } from "@couples/shared";

import { supabase } from "../supabase";

interface AnniversaryRow {
  id: string;
  couple_id: string;
  title: string;
  base_date: string;
  kind: AnniversaryKind;
  icon?: AnniversaryIcon | null;
  memo?: string | null;
}

const COLUMNS = "id, couple_id, title, base_date, kind, icon, memo";

function toAnniversary(row: AnniversaryRow): Anniversary {
  return {
    id: row.id,
    coupleId: row.couple_id,
    title: row.title,
    baseDate: row.base_date,
    kind: row.kind,
    icon: row.icon ?? "heart",
    memo: row.memo ?? null,
  };
}

export async function fetchAnniversaries(): Promise<Anniversary[]> {
  // icon/memo 列がまだ無い環境(マイグレーション適用前)でも一覧が壊れないよう、
  // 列を名指しせずに取り、無ければ既定値扱いにする
  const { data, error } = await supabase.from("anniversaries").select("*").order("base_date");
  if (error) throw error;
  const rows = data as AnniversaryRow[];
  return rows.map(toAnniversary);
}

export interface AnniversaryInput {
  title: string;
  /** `YYYY-MM-DD` */
  baseDate: string;
  kind: AnniversaryKind;
  icon: AnniversaryIcon;
  /** 空欄は null */
  memo: string | null;
}

/** couple_id は DB 側の既定値でセッションから埋まる(→ 20260813000011_schedule_defaults.sql) */
export async function createAnniversary(input: AnniversaryInput): Promise<Anniversary> {
  const { data, error } = await supabase
    .from("anniversaries")
    .insert({
      title: input.title,
      base_date: input.baseDate,
      kind: input.kind,
      icon: input.icon,
      memo: input.memo,
    })
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toAnniversary(data);
}

export async function updateAnniversary(id: string, input: AnniversaryInput): Promise<Anniversary> {
  const { data, error } = await supabase
    .from("anniversaries")
    .update({
      title: input.title,
      base_date: input.baseDate,
      kind: input.kind,
      icon: input.icon,
      memo: input.memo,
    })
    .eq("id", id)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toAnniversary(data);
}

export async function deleteAnniversary(id: string): Promise<void> {
  const { error } = await supabase.from("anniversaries").delete().eq("id", id);
  if (error) throw error;
}
